import { NorthEast, SouthEast } from '@mui/icons-material';
import { Radio, RadioGroup, Stack, styled, Typography } from '@mui/material';
import { CardSection, CardSubSection, Show, Tile } from '@side/pantry';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useAgentDslFilters } from '@/common/hooks/useAgentDslFilters';
import {
  TrackReferralsEvent,
  useTrackReferralsAnalytics,
} from '$analytics/useTrackAnalytics/domains/referrals';
import { AgentAutocomplete } from '$form-fields';
import { useReferralContext } from '../../common/ReferralContext';
import {
  findAgentTeamMembership,
  getFirstAgent,
  getFirstAgentFieldName,
  getIsSending,
  getSecondAgentFieldName,
} from '../../utils';
import {
  baseAgentFields,
  type FieldValues,
} from '../../views/NewReferral/types';
import { BothSideAgentsBanner } from '../BothSideAgentsBanner';
import { AgentSyncBanner } from './AgentSyncBanner';
import { SecondAgentDetails } from './SecondAgentDetails';

const StyledTile = styled(Tile)(({ theme }) => ({
  '&.overrideDisabled': {
    outlineColor: theme.palette.primary.main,
    backgroundColor: theme.palette.action.selected,

    '& .MuiTypography-root': {
      color: theme.palette.primary.main,
    },

    '& .MuiSvgIcon-root': {
      fill: theme.palette.primary.main,
    },
  },
}));

type AgentDetailsProps = {
  onUpdate?: () => void;
  hideReferralType?: boolean;
};

export function AgentDetails({
  onUpdate,
  hideReferralType,
}: AgentDetailsProps) {
  const { submitAsAgents, registerSelectedAgent } = useReferralContext();
  const { register, resetField } = useFormContext<FieldValues>();
  const trackEvent = useTrackReferralsAnalytics();

  const [referralType, referringAgent, transactingAgent, id] = useWatch<
    FieldValues,
    ['referralType', 'referringAgent', 'transactingAgent', 'id']
  >({
    name: ['referralType', 'referringAgent', 'transactingAgent', 'id'],
  });

  const isSending = getIsSending({ referralType });
  const disableSending = !!id && isSending;
  const disableReceiving = !!id && !isSending;
  const firstAgentFieldName = getFirstAgentFieldName({ referralType });
  const secondAgentFieldName = getSecondAgentFieldName({ referralType });
  const firstAgent = getFirstAgent({
    referralType,
    referringAgent,
    transactingAgent,
  });

  const selectedAgent = findAgentTeamMembership(firstAgent, submitAsAgents);

  const agentDslFilters = useAgentDslFilters();
  const sendingDslFilters = useAgentDslFilters({
    roles: ['AGENT', 'REFERRAL_AGENT'],
  });

  // Check userId instead of isSideAgent because isSideAgent isn't set
  // reliably on the Create page (see SecondAgentDetails).
  const bothSideAgents =
    Boolean(referringAgent?.userId) && Boolean(transactingAgent?.userId);

  return (
    <Stack sx={{ gap: { xs: 2.5, md: 3.75 } }}>
      {/* An agent the form doesn't have yet has no fields to diff against
          Identity, which is what an empty partial already means here. */}
      <AgentSyncBanner
        referringAgent={referringAgent ?? {}}
        transactingAgent={transactingAgent ?? {}}
        referralType={referralType}
        referralId={id}
        sx={{ m: 0 }}
      />

      <Show when={!hideReferralType}>
        <CardSection cardTitle="Referral Type">
          <CardSubSection>
            <Stack sx={{ gap: 3 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Select the referral type and then the agent on your team
              </Typography>
              {/**
               * After the Referral has been saved the checked radio button should have
               * the active "checked" style, despite being disabled. referralType is immutable.
               */}
              <RadioGroup
                aria-label="Referral Type"
                onChange={(e) => {
                  trackEvent(TrackReferralsEvent.SelectReferralType, {
                    value: e.target.value,
                  });
                  resetField(firstAgentFieldName);
                  onUpdate?.();
                }}
                row
                sx={{
                  gap: 2,
                  maxWidth: 600,
                  width: '100%',
                  '& label': {
                    margin: 0,
                  },
                }}
                value={referralType}
              >
                <StyledTile
                  className={disableSending ? 'overrideDisabled' : undefined}
                  control={
                    <Radio
                      {...register('referralType')}
                      checkedIcon={<NorthEast />}
                      icon={<NorthEast />}
                    />
                  }
                  label="Sending"
                  value="sending"
                  disabled={disableReceiving}
                />

                <StyledTile
                  className={disableReceiving ? 'overrideDisabled' : undefined}
                  control={
                    <Radio
                      {...register('referralType')}
                      checkedIcon={<SouthEast />}
                      icon={<SouthEast />}
                    />
                  }
                  label="Receiving"
                  value="receiving"
                  disabled={disableSending}
                />
              </RadioGroup>
            </Stack>
          </CardSubSection>
        </CardSection>
      </Show>

      <Stack>
        <CardSection cardTitle="Agent Details">
          <CardSubSection>
            <Controller<FieldValues, typeof firstAgentFieldName>
              key={firstAgentFieldName}
              name={firstAgentFieldName}
              shouldUnregister={false}
              rules={{
                validate: (value) => {
                  if (!value?.userId || !value?.teamId) {
                    return 'Required';
                  }
                  return true;
                },
              }}
              render={({ field, fieldState }) => (
                <AgentAutocomplete
                  id={firstAgentFieldName}
                  onChange={(_, agent) => {
                    if (agent?.id) {
                      registerSelectedAgent(agent);
                    }
                    // Clear stale profile fields on selection so the form
                    // doesn't carry the previous agent's data into the
                    // sync-banner diff. The PATCH response repopulates them
                    // from Identity.
                    field.onChange({
                      ...baseAgentFields,
                      id: field.value?.id,
                      teamId: agent?.teamIdentityId ?? '',
                      userId: agent?.userIdentityId ?? '',
                      isSideAgent: Boolean(agent?.id),
                    });

                    onUpdate?.();

                    trackEvent(
                      TrackReferralsEvent.SelectPrimaryAgentSuggestion,
                      {
                        contactId: agent?.id,
                        eventTarget: isSending
                          ? 'referring_agent_dropdown'
                          : 'transacting_agent_dropdown',
                      },
                    );
                  }}
                  selectedAgent={selectedAgent}
                  TextFieldProps={{
                    label: `${isSending ? 'Referring' : 'Transacting'} Agent`,
                    placeholder: 'Search and select',
                    required: true,
                    highlight: selectedAgent ? undefined : 'secondary',
                    error: fieldState.invalid,
                    helperText: fieldState.error?.message,
                    onBlur: field.onBlur,
                  }}
                  filters={
                    referralType === 'sending'
                      ? sendingDslFilters
                      : agentDslFilters
                  }
                />
              )}
            />
          </CardSubSection>

          <SecondAgentDetails key={secondAgentFieldName} onUpdate={onUpdate} />
        </CardSection>

        {bothSideAgents && <BothSideAgentsBanner />}
      </Stack>
    </Stack>
  );
}
